/**
 * 3D rotation matrix functions using gl-matrix
 *
 * Ported from Python (map_projection/earth_model.py lines 64-78)
 */

import { mat3 } from 'gl-matrix';

/**
 * Create a 3D rotation matrix from Euler angles (ZYX order)
 * Matches the rotation matrix from Python earth_model.py
 *
 * @param {number} alpha - Rotation around Z axis (degrees)
 * @param {number} beta - Rotation around Y axis (degrees)
 * @param {number} gamma - Rotation around X axis (degrees)
 * @returns {mat3} 3x3 rotation matrix (gl-matrix format, column-major)
 */
export function createRotationMatrix(alpha, beta, gamma) {
    // Convert degrees to radians
    const alphaRad = alpha * Math.PI / 180;
    const betaRad = beta * Math.PI / 180;
    const gammaRad = gamma * Math.PI / 180;

    // Precompute trigonometric values
    const cosa = Math.cos(alphaRad);
    const sina = Math.sin(alphaRad);
    const cosb = Math.cos(betaRad);
    const sinb = Math.sin(betaRad);
    const cosg = Math.cos(gammaRad);
    const sing = Math.sin(gammaRad);

    // Build rotation matrix (matches Python exactly)
    // Python uses: np.dot(points, R) which means points as row vectors
    // JavaScript uses: R * points which means points as column vectors
    // These are equivalent to using the transpose, so we need to transpose the Python matrix
    //
    // Python matrix (row-major):
    const R00 = cosb * cosg;
    const R01 = sina * sinb * cosg - cosa * sing;
    const R02 = cosa * sinb * cosg + sina * sing;
    const R10 = cosb * sing;
    const R11 = sina * sinb * sing + cosa * cosg;
    const R12 = cosa * sinb * sing - sina * cosg;
    const R20 = -sinb;
    const R21 = sina * cosb;
    const R22 = cosa * cosb;

    // Create transposed matrix for gl-matrix
    // gl-matrix expects column-major, so to create R^T we pass it row-by-row
    const R = mat3.fromValues(
        R00, R01, R02,  // Python row 0 becomes column 0 of R^T
        R10, R11, R12,  // Python row 1 becomes column 1 of R^T
        R20, R21, R22   // Python row 2 becomes column 2 of R^T
    );

    return R;
}

/**
 * Apply rotation matrix to an array of 3D points
 *
 * @param {Float32Array} points - Flat array [x1,y1,z1, x2,y2,z2, ...]
 * @param {mat3} matrix - 3x3 rotation matrix from createRotationMatrix
 * @returns {Float32Array} Rotated points in same format
 */
export function rotatePoints(points, matrix) {
    const numPoints = points.length / 3;
    const rotated = new Float32Array(points.length);

    for (let i = 0; i < numPoints; i++) {
        const offset = i * 3;
        const x = points[offset];
        const y = points[offset + 1];
        const z = points[offset + 2];

        // Matrix multiplication: [x', y', z'] = R * [x, y, z]
        // gl-matrix is column-major:
        // R = [m0, m1, m2,   <- first column
        //      m3, m4, m5,   <- second column
        //      m6, m7, m8]   <- third column
        rotated[offset] = matrix[0] * x + matrix[3] * y + matrix[6] * z;
        rotated[offset + 1] = matrix[1] * x + matrix[4] * y + matrix[7] * z;
        rotated[offset + 2] = matrix[2] * x + matrix[5] * y + matrix[8] * z;
    }

    return rotated;
}
